import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { useOrganization } from '../../hooks/useOrganization';
import { formatCurrency } from '../../utils/currency';

const PurchaseReturnForm = ({ onClose, onSuccess }) => {
  const { organization } = useOrganization();
  const [formData, setFormData] = useState({
    vendorId: '',
    purchaseOrderId: '',
    reason: '',
    status: 'PENDING'
  });

  const [items, setItems] = useState([
    { partId: '', partName: '', quantity: 1, unitPrice: 0, reason: 'Defective' }
  ]);

  const [vendors, setVendors] = useState([]);
  const [parts, setParts] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const returnReasons = [
    'Defective',
    'Wrong Item',
    'Damaged in Transit',
    'Expired',
    'Overstock',
    'Quality Issues',
    'Not as Described',
    'Other'
  ];

  useEffect(() => {
    fetchVendors();
    fetchParts();
  }, []);

  useEffect(() => {
    if (formData.vendorId) {
      fetchPurchaseOrders(formData.vendorId);
    }
  }, [formData.vendorId]);

  useEffect(() => {
    if (formData.purchaseOrderId) {
      fetchPOItems(formData.purchaseOrderId);
    }
  }, [formData.purchaseOrderId]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors');
      setVendors(response.data.vendors || []);
    } catch (error) {
    }
  };

  const fetchParts = async () => {
    try {
      const response = await axios.get('/api/parts');
      setParts(response.data.parts || []);
    } catch (error) {
    }
  };

  const fetchPurchaseOrders = async (vendorId) => {
    try {
      const response = await axios.get(`/api/purchase-orders?vendorId=${vendorId}&status=RECEIVED`);
      setPurchaseOrders(response.data.purchaseOrders || []);
    } catch (error) {
      setPurchaseOrders([]);
    }
  };

  const fetchPOItems = async (poId) => {
    try {
      const response = await axios.get(`/api/purchase-orders/${poId}`);
      const po = response.data;
      if (po.items && po.items.length > 0) {
        const poItems = po.items.map(item => ({
          partId: item.partId,
          partName: item.partName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          reason: 'Defective'
        }));
        setItems(poItems);
      }
    } catch (error) {
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (field === 'partId' && value) {
      const selectedPart = parts.find(p => p.id === value);
      if (selectedPart) {
        newItems[index].partName = selectedPart.name;
        newItems[index].unitPrice = parseFloat(selectedPart.costPrice || selectedPart.price || 0);
      }
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { partId: '', partName: '', quantity: 1, unitPrice: 0, reason: 'Defective' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const returnData = {
        vendorId: parseInt(formData.vendorId),
        purchaseOrderId: parseInt(formData.purchaseOrderId),
        reason: formData.reason,
        status: formData.status,
        items: items.filter(item => item.partId && item.quantity > 0).map(item => ({
          partId: parseInt(item.partId),
          partName: item.partName,
          quantity: parseInt(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          reason: item.reason
        }))
      };

      if (returnData.items.length === 0) {
        toast.error('Please add at least one item to return');
        setLoading(false);
        return;
      }

      await axios.post('/api/purchase-returns', returnData);
      toast.success('Purchase return created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create return');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Create Purchase Return</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor *
              </label>
              <select
                value={formData.vendorId}
                onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Vendor</option>
                {vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Order *
              </label>
              <select
                value={formData.purchaseOrderId}
                onChange={(e) => setFormData({ ...formData, purchaseOrderId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Purchase Order</option>
                {purchaseOrders.map(po => (
                  <option key={po.id} value={po.id}>{po.orderNo}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return Reason
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows="3"
              placeholder="Describe the reason for this return..."
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Return Items</h3>
              <button
                type="button"
                onClick={addItem}
                className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-12 gap-3 items-center font-medium text-sm text-gray-700 border-b pb-2">
                <div className="col-span-4">Part</div>
                <div className="col-span-2">Quantity</div>
                <div className="col-span-2">Unit Price</div>
                <div className="col-span-3">Reason</div>
                <div className="col-span-1">Action</div>
              </div>

              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-4">
                    <select
                      value={item.partId}
                      onChange={(e) => handleItemChange(index, 'partId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Part</option>
                      {parts.map(part => (
                        <option key={part.id} value={part.id}>
                          {part.name} (Stock: {part.quantity})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="1"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="col-span-3">
                    <select
                      value={item.reason}
                      onChange={(e) => handleItemChange(index, 'reason', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {returnReasons.map(reason => (
                        <option key={reason} value={reason}>{reason}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-900"
                      disabled={items.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <div className="text-lg font-semibold">
                Total: {formatCurrency(calculateTotal(), organization?.currency_code)}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Return'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseReturnForm;
